<template>
  <div>
    <Head title="Transferencia de Productos" />
    
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold">
        Transferencia de Productos - {{ deposit.name }}
      </h1>
    </div>

    <!-- Búsqueda de Productos -->
    <div class="mb-6">
      <div class="flex flex-wrap gap-4">
        <text-input 
          v-model="form.search.terms"
          label="Buscar por nombre o código"
          class="w-full md:w-1/3"
          placeholder="Ingrese término de búsqueda..."
        />
      </div>
    </div>

    <!-- Lista de Productos Disponibles -->
    <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
      <div class="p-4 bg-gray-50 border-b">
        <h2 class="text-lg font-semibold text-gray-800">Productos Disponibles</h2>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Código</th>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Producto</th>
              <th class="px-4 py-2 text-center text-sm font-medium text-gray-600">Stock Disponible</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="product in products.data" 
              :key="product.id"
              class="hover:bg-gray-50 cursor-pointer"
              @click="getProductStock(product.depositInventory, deposit.id) > 0 ? selectProduct(product) : alert('No hay stock disponible')"
            >
              <td class="px-4 py-2 text-sm text-gray-900">{{ product.code }}</td>
              <td class="px-4 py-2 text-sm text-gray-900">{{ product.name }}</td>
              <td class="px-4 py-2 text-sm text-center text-gray-900">
                {{ getProductStock(product.depositInventory, deposit.id) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Productos Seleccionados -->
    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="p-4 bg-gray-50 border-b">
        <h2 class="text-lg font-semibold text-gray-800">Productos a Transferir</h2>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Producto</th>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Depósito Destino</th>
              <th class="px-4 py-2 text-center text-sm font-medium text-gray-600">Cantidad</th>
              <th class="px-4 py-2 text-center text-sm font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in form.products" :key="product.id">
              <td class="px-4 py-2 text-sm text-gray-900">
                {{ product.name }}
              </td>
              <td class="px-4 py-2">
                <select-input 
                  v-model="form.products[index].deposit"
                  option-label="name"
                  option-value="id"
                  class="w-full"
                  placeholder="Seleccione un depósito"
                >
                  <option v-for="deposit in filteredDeposits" :key="deposit.id" :value="deposit.id">
                    {{ deposit.name }}
                  </option>
                </select-input>
              </td>
              <td class="px-4 py-2">
                <text-input 
                  v-model="form.products[index].quantity"
                  type="number"
                  :max="getProductStock(product.depositInventory, deposit.id)"
                  min="1"
                  class="w-full"
                />
              </td>
              <td class="px-4 py-2 text-center">
                <button 
                  class="text-red-600 hover:text-red-800"
                  @click="removeProduct(product.id)"
                  title="Eliminar producto"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="p-4 bg-gray-50 border-t">
        <button 
          class="btn-indigo"
          :disabled="!canSubmit"
          @click="submitTransfer"
        >
          Generar Transferencia
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import SelectInput from '@/Shared/SelectInput.vue'
import TextInput from '@/Shared/TextInput'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

export default {
  name: 'TransferCreate',

  components: {
    Head,
    Link,
    SelectInput,
    TextInput,
  },

  layout: Layout,

  props: {
    depositTransfers: Object,
    depositId: Number,
    products: Object,
    brands: Object,
    parts: Object,
    models: Object,
    deposits: Object,
    regions: Object,
    deposit: Object,
  },

  data() {
    return {
      form: this.$inertia.form({
        search: {
          terms: '',
          deposit: this.depositId,
          brand_id: '',
          part_id: '',
          truck_model_id: '',
        },
        products: [],
      }),
    }
  },

  computed: {
    canSubmit() {
      return this.form.products.length > 0 && 
        this.form.products.every(product => 
          product.quantity > 0 && 
          product.deposit && 
          product.quantity <= this.getProductStock(product.depositInventory, this.deposit.id)
        )
    },

    filteredDeposits() {
      return this.deposits.filter(deposit => deposit.id !== this.deposit.id)
    }
  },

  watch: {
    'form.search': {
      handler: throttle(function() {
        this.onSearchProductsChange()
      }, 300),
      deep: true
    }
  },

  methods: {
    alert(message) {
      this.$swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: message,
      })
    },
    getProductStock(inventory, depositId) {
      const stock = inventory.find(inv => inv.deposit_id === depositId)
      return stock ? stock.quantity : 0
    },

    onSearchProductsChange() {
      this.$inertia.get(
        `/deposits/${this.depositId}/transfer/create`,
        pickBy(this.form.search),
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        }
      )
    },

    selectProduct(product) {
      const exists = this.form.products.some(p => p.id === product.id)
      if (!exists) {
        this.form.products.push({
          ...product,
          quantity: 0,
          deposit: null,
        })
      }
    },

    removeProduct(productId) {
      if (confirm('¿Está seguro que desea eliminar este producto de la transferencia?')) {
        this.form.products = this.form.products.filter(p => p.id !== productId)
      }
    },

    submitTransfer() {
      if (!this.canSubmit) {
        alert('Por favor, complete todos los campos requeridos y verifique las cantidades.')
        return
      }

      this.form.post(`/deposits/${this.depositId}/transfer`)
    }
  }
}
</script>
